import React, {useEffect, useState} from 'react';
import {Button, Form, Input, Modal, Row, Col, Checkbox, DatePicker, message, AutoComplete} from 'antd';
import {CommandDto, ImportTestDataCommandDto, TestControllerApi, TestDto} from '../../../api';
import {EditOutlined, ImportOutlined} from "@ant-design/icons";
import {getLabelWithNameAndInfotext} from "../../../utils/Utils";
import TextArea from "antd/es/input/TextArea";
import {useKeycloakHeader} from "../../../config/Config";


const ImportTestDataCommandModal = ({onCommandCardData, initialData}: {
    onCommandCardData: Function;
    initialData?: CommandDto
}) => {
    const [form] = Form.useForm();
    const [isModalVisible, setIsModalVisible] = useState(false); // State für die Modal-Sichtbarkeit
    const [tests, setTests] = useState<TestDto[]>([]);
    const [searchOptions, setSearchOptions] = useState<{ value: string }[]>([]);
    const [filteredOptions, setFilteredOptions] = useState<{ value: string }[]>([]);
    const [name, setName] = useState<string>('');
    const [selectedTest, setSelectedTest] = useState<TestDto>();

    const testControllerApi = new TestControllerApi();
    const headers = useKeycloakHeader();

    useEffect(() => {
        // Wenn initialData vorhanden ist, fülle das Formular mit den initialen Daten
        if (initialData && initialData.importTestDataCommandDto) {
            let data = initialData.importTestDataCommandDto;
            form.setFieldsValue({
                testId: data.testId,
                containsVariables: data.containsVariables ? data.containsVariables.join(',') : null,
                useFailedTestExecution: !!data.useFailedTestExecution,
                useUsedTestExecution: !!data.useUsedTestExecution,
                //startTs: data.startTs,
                days: data.timeOffset?.days,
                hours: data.timeOffset?.hours,
                minutes: data.timeOffset?.minutes,
                seconds: data.timeOffset?.seconds,
            });
        }
        fetchTests();
    }, [initialData, form]);

    const onFinish = (values) => {
        let days = values.days;
        let hours = values.hours;
        let minutes = values.minutes;
        let seconds = values.seconds;

        let hasTimeOffset = days || hours || minutes || seconds;
        if(hasTimeOffset && values.startTs){
            message.error("Startzeit und Zeit-Offset können nicht gleichzeitig verwendet werden!")
            return;
        }

        let timeOffset;
        if (hasTimeOffset) {
            days = days ? days : 0;
            hours = hours ? hours : 0;
            minutes = minutes ? minutes : 0;
            seconds = seconds ? seconds : 0;
            timeOffset = {days: days, hours: hours, minutes: minutes, seconds: seconds};
        }



        let importCommand: ImportTestDataCommandDto = {
            testId: selectedTest ? selectedTest.id : values.testId,
            containsVariables: typeof values.containsVariables === 'string'
                ? values.containsVariables.split(',')
                : null,
            useFailedTestExecution: !!values.useFailedTestExecution,
            useUsedTestExecution: !!values.useUsedTestExecution,
            startTs: values.startTs,
            timeOffset: timeOffset
        };

        let command: CommandDto = {
            commandType: 'IMPORT',
            description: values.description,
            importTestDataCommandDto: importCommand
        };

        onCommandCardData(command);
        setIsModalVisible(false); // Modal schließen
    };

    const onReset = () => {
        form.resetFields();
    };

    // Funktion zum Öffnen des Modals
    const showModal = () => {
        setIsModalVisible(true);
    };

    // Funktion zum Schließen des Modals
    const handleCancel = () => {
        setIsModalVisible(false);
    };

    const fetchTests = () => {
        testControllerApi.getAllTestsMinimal(headers).then((axiosResponse) => {
            if (axiosResponse.status === 200) {
                const testDtos: TestDto[] = axiosResponse.data;
                setTests(testDtos);

                const options = testDtos.map((test) => ({value: test.name || ''}));
                setSearchOptions(options);
                setFilteredOptions(options);
            }
        }).catch((error) => {
            message.error("Fehler beim Abrufen von Tests!");
        });
    };

    const handleNameChange = (value: string) => {
        setName(value);
        const filteredOptions = searchOptions.filter(option =>
            option.value.toLowerCase().includes(value.toLowerCase())
        );
        setFilteredOptions(filteredOptions);
    };

    const handleSelectedTest = (value: string) => {
        const selectedTest = tests.find(test => test.name === value);
        setSelectedTest(selectedTest);
    }


    return (
        <>
            {initialData == null &&
                <div style={{display: 'flex', alignItems: 'stretch', justifyContent: 'stretch'}}>
                  <Button
                      type="primary"
                      onClick={showModal}
                      style={{width: '100%', height: '100%'}}

                  >
                    <ImportOutlined/> Import
                  </Button>
                </div>}
            {initialData != null &&
                <Button
                    type="primary"
                    style={{float: 'right'}}
                    onClick={showModal}
                >
                  <EditOutlined/>
                </Button>
            }
            <Modal
                title="Import von Testdaten"
                open={isModalVisible}
                onCancel={handleCancel}
                footer={null}
            >
                <Form
                    form={form}
                    name="control-hooks"
                    onFinish={onFinish}
                    style={{maxWidth: 600}}
                    layout="vertical"
                >
                    {/* Test ID */}
                    <Form.Item name="testId" label="Test-ID" rules={[{required: true, message: 'Bitte eine Test-ID angeben!'}]}>
                        <AutoComplete
                            options={filteredOptions}
                            placeholder="Suche nach Test"
                            onSelect={handleSelectedTest}
                            style={{marginBottom: 16, width: '100%'}}
                            onChange={handleNameChange}
                            value={name}
                        />
                    </Form.Item>

                    {/* Contains Variables */}
                    <Form.Item
                        name="containsVariables"
                        label={getLabelWithNameAndInfotext(
                            "Erforderliche Variablen",
                            "Die Testläufe lassen sich anhand der hier angegebenen Variablen filtern",
                            "Auf diese Weise wird sichergestellt, dass die Variablen tatsächlich im Test vorhanden sind."
                        )}
                    >
                        <TextArea rows={4} placeholder="Variablen eingeben, mit Komma separiert"/>
                    </Form.Item>

                    {/* Use Failed Test Execution and Use Used Test Execution (Checkboxes Nebeneinander) */}
                    <Form.Item label="Optionen">
                        <Row gutter={16}>
                            <Col>
                                <Form.Item
                                    name="useFailedTestExecution"
                                    valuePropName="checked"
                                    noStyle
                                >
                                    <Checkbox>Fehlgeschlagene Testausführungen?</Checkbox>
                                </Form.Item>
                            </Col>
                            <Col>
                                <Form.Item
                                    name="useUsedTestExecution"
                                    valuePropName="checked"
                                    noStyle
                                >
                                    <Checkbox>Verwendete Testausführungen?</Checkbox>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form.Item>

                    {/* Start Timestamp */}
                    <Form.Item
                        name="startTs"
                        label={getLabelWithNameAndInfotext(
                            "Startzeit",
                            "Ab welchen Zeitpunkt sollen die Testausführungen berücksichtigt werden? (Statisch)"
                        )}
                    >
                        <DatePicker placeholder={"Startdatum auswählen"} showTime format="YYYY-MM-DD HH:mm:ss" style={{width: '100%'}}/>
                    </Form.Item>

                    {/* Time Offset (Vier Kleine Felder für Tage, Stunden, Minuten und Sekunden) */}
                    <Form.Item
                        name="timeOffset"
                        label={getLabelWithNameAndInfotext(
                            "Zeit-Offset",
                            "Ab welchen Zeitpunkt sollen die Testausführungen berücksichtigt werden? (Dynamisch)"
                        )}
                    >
                        <Row gutter={8}>
                            <Col span={6}>
                                <Form.Item name="days" noStyle>
                                    <Input placeholder="Tage"/>
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item name="hours" noStyle>
                                    <Input placeholder="Stunden"/>
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item name="minutes" noStyle>
                                    <Input placeholder="Minuten"/>
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item name="seconds" noStyle>
                                    <Input placeholder="Sekunden"/>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form.Item>

                    <Form.Item>
                        <Row justify="center">
                            <Col style={{marginRight: 5}}>
                                <Button type="primary" htmlType="submit">
                                    {initialData ? "Speichern" : "Hinzufügen"}
                                </Button>
                            </Col>
                            <Col>
                                <Button htmlType="button" onClick={onReset}>
                                    Zurücksetzen
                                </Button>
                            </Col>
                        </Row>
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default ImportTestDataCommandModal;
